import * as React from "react";
import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { createStep3Schema, validateStep } from "../../../validation/abstractSchema";
import { toastMultilineErrors } from "../../../util";

interface Step3Props {
  iconSideToSide: string;
  iconUpAndDown: string;
  formData: any;
  setFormData: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

const Step3: React.FC<Step3Props> = ({
  iconSideToSide,
  iconUpAndDown,
  formData,
  setFormData,
  onNext,
  onBack,
}) => {
  /***************************
   * State Variables
   ***************************/
  const [title, setTitle] = useState(formData.title || "");
  const [content, setContent] = useState(formData.content || "");
  const [abstractImages, setAbstractImages] = useState(
    formData.abstractImages || [],
  );
  const [imagePosition, setImagePosition] = useState(
    formData.imagePosition || "side_to_side",
  );
  const [keywords, setKeywords] = useState(formData.keywords || [""]);
  const [references, setReferences] = useState(formData.references || [""]);
  const [referenceWordCounts, setReferenceWordCounts] = useState(
    references.map(() => 0),
  );
  const [titleCharCount, setTitleCharCount] = useState(0);
  const [contentWordCount, setContentWordCount] = useState(0);
  const [showAllSymbolPopup, setShowAllSymbolPopup] = useState(false);
  const [activeFieldForSymbols, setActiveFieldForSymbols] = useState("");
  const [lastInsertedSymbol, setLastInsertedSymbol] = useState("");
  // State to track if special characters have been added
  const [symbolsAdded, setSymbolsAdded] = useState({
    title: false,
    content: false,
  });

  /***************************
   * Ref
   ***************************/
  // Refs for Quill editors
  const titleQuillRef = useRef<ReactQuill>(null);
  const contentQuillRef = useRef<ReactQuill>(null);

  /***************************
   * Constants
   ***************************/
  const MAX_TITLE_CHARS = 500;
  const MAX_CONTENT_WORDS = 310;
  const MAX_IMAGES = 2;
  const MAX_KEYWORDS = 5;
  const MIN_KEYWORDS = 1;
  const MAX_REFERENCES = 5;
  const MIN_REFERENCES = 0;
  const MAX_REFERENCES_WORDS = 50;

  const specialSymbols = [
    { char: "α", name: "Alpha" },
    { char: "β", name: "Beta" },
    { char: "γ", name: "Gamma" },
    { char: "δ", name: "Delta" },
    { char: "°", name: "Degree" },
    { char: "±", name: "Plus-minus" },
    { char: "≥", name: "Greater than or equal" },
    { char: "≤", name: "Less than or equal" },
    { char: "≠", name: "Not equal" },
    { char: "≈", name: "Approximately equal" },
    { char: "∞", name: "Infinity" },
    { char: "µ", name: "Mu" },
    { char: "π", name: "Pi" },
    { char: "∑", name: "Summation" },
    { char: "∆", name: "Delta" },
  ];

  // Quill modules configuration - using standard toolbar without custom symbols
  const quillModules = {
    toolbar: [
      ["bold", "italic"],
      [{ script: "sub" }, { script: "super" }],
      ["clean"], // remove formatting button
    ],
    clipboard: {
      matchVisual: false,
    },
    keyboard: {
      bindings: {
        "list autofill": null, // Disables auto-numbered list on "1. "
        "bullet list": null, // Disables auto-bullet list on "- "
        "ordered list": null, // Additional safeguard for ordered lists
      },
    },
  };

  /***************************
   * Handlers
   ***************************/
  // Count characters and words
  const updateTitleCharCount = (content: string) => {
    // Strip HTML tags and decode entities, then count characters
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    setTitleCharCount(textContent.length);
  };

  const updateContentWordCount = (content: string) => {
    // Strip HTML tags and decode entities, then count words
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    const words = textContent
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
    setContentWordCount(words.length);
  };

  // Function to add a special symbols popup button to Quill toolbar
  const addSymbolPopupButton = (
    quill: any,
    symbols: any[],
    fieldId: string,
  ) => {
    // Use setTimeout to ensure the toolbar is fully rendered
    setTimeout(() => {
      const toolbar = quill.getModule("toolbar");
      if (toolbar) {
        const toolbarElement = toolbar.container;

        // Check if we already added the button to avoid duplicates
        if (toolbarElement.querySelector(".symbol-popup")) {
          return;
        }

        // Add a container for the special symbols button
        const symbolButtonContainer = document.createElement("span");
        symbolButtonContainer.className = "ql-formats symbol-popup";

        const button = document.createElement("button");
        button.type = "button";
        button.className = "ql-symbol-button";
        button.innerHTML = "Ω";
        button.title = "Special Symbols";
        button.style.fontFamily = "Times New Roman, serif";
        button.style.fontSize = "16px";
        button.style.padding = "4px 8px";
        button.style.margin = "0 1px";
        button.style.borderRadius = "4px";
        button.style.cursor = "pointer";
        button.style.fontWeight = "bold";
        button.style.display = "flex";
        button.style.alignItems = "center";
        button.style.justifyContent = "center";

        button.addEventListener("mouseenter", () => {
          button.style.background = "#f3f4f6";
          button.style.borderColor = "#9ca3af";
        });

        button.addEventListener("mouseleave", () => {
          button.style.background = "#f9fafb";
          button.style.borderColor = "#d1d5db";
        });

        button.addEventListener("click", () => {
          setActiveFieldForSymbols(fieldId);
          setShowAllSymbolPopup(true);
        });

        symbolButtonContainer.appendChild(button);

        // Append to toolbar
        toolbarElement.appendChild(symbolButtonContainer);
      }
    }, 100); // Small delay to ensure DOM is ready
  };

  // Insert special symbol from popup
  const insertSymbol = (char: string) => {
    let quillInstance = null;

    if (activeFieldForSymbols === "title") {
      quillInstance = titleQuillRef.current;
    } else if (activeFieldForSymbols === "content") {
      quillInstance = contentQuillRef.current;
    }

    if (quillInstance) {
      // Get the underlying Quill editor instance
      const quill = quillInstance.getEditor();

      if (quill) {
        const range = quill.getSelection();

        if (range) {
          quill.insertText(range.index, char, "user");
          quill.setSelection({ index: range.index + char.length, length: 0 });
        } else {
          // If no selection, insert at the end
          const length = quill.getLength();
          const insertPosition = length > 0 ? length - 1 : 0;
          quill.insertText(insertPosition, char, "user");
          quill.setSelection({ index: insertPosition + char.length, length: 0 });
        }

        // Focus the editor
        quill.focus();

        // Show visual feedback
        setLastInsertedSymbol(char);
        setTimeout(() => setLastInsertedSymbol(""), 500);
      } else {
        console.log("Could not get Quill editor instance");
      }
    } else {
      console.log("No Quill instance found for field:", activeFieldForSymbols);
    }
    // Don't close the popup - let user continue inserting symbols
  };

  // Image handling
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (abstractImages.length + files.length > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    const newAbstractImages = [...abstractImages];

    files.forEach((file) => {
      const preview = URL.createObjectURL(file);
      const position = newAbstractImages.length + 1;
      newAbstractImages.push({
        file,
        preview,
        title: "",
        position,
      });
    });

    setAbstractImages(newAbstractImages);
  };

  const handleRemoveImage = (index: number) => {
    const newAbstractImages = [...abstractImages];

    URL.revokeObjectURL(newAbstractImages[index].preview);
    newAbstractImages.splice(index, 1);

    const repositionedImages = newAbstractImages.map((img, idx) => ({
      ...img,
      position: idx + 1,
    }));

    setAbstractImages(repositionedImages);
  };

  const handleTitleChange = (index: number, value: string) => {
    const newAbstractImages = [...abstractImages];
    newAbstractImages[index].title = value;
    setAbstractImages(newAbstractImages);
  };

  // Keywords handling
  const handleAddKeyword = () => {
    if (keywords.length < MAX_KEYWORDS) {
      setKeywords([...keywords, ""]);
    }
  };

  const handleRemoveKeyword = (index: number) => {
    if (keywords.length > MIN_KEYWORDS) {
      const newKeywords = [...keywords];
      newKeywords.splice(index, 1);
      setKeywords(newKeywords);
    }
  };

  const handleKeywordChange = (index: number, value: string) => {
    const newKeywords = [...keywords];
    newKeywords[index] = value;
    setKeywords(newKeywords);
  };

  // References handling
  const handleAddReference = () => {
    if (references.length < MAX_REFERENCES) {
      setReferences([...references, ""]);
    }
  };

  const handleRemoveReference = (index: number) => {
    if (references.length > MIN_REFERENCES) {
      const newReferences = [...references];
      newReferences.splice(index, 1);
      setReferences(newReferences);
    }
  };

  const handleReferenceChange = (index: number, value: string) => {
    const newReferences = [...references];
    newReferences[index] = value;
    setReferences(newReferences);
  };

  const handleNext = () => {
    const stepData = {
      title,
      content,
      abstractImages,
      imagePosition,
      keywords: keywords.filter((k: string) => k.trim()),
      references: references.filter((r: string) => r.trim()),
    };

    const validation = validateStep(stepData, createStep3Schema());
    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors || {});
      toastMultilineErrors(errorMessages);
      return;
    }

    const filledKeywords = keywords.filter((k: string) => k.trim());
    const filledReferences = references.filter((r: string) => r.trim());
    setFormData({
      ...formData,
      title,
      content,
      abstractImages,
      imagePosition,
      keywords: filledKeywords,
      references: filledReferences,
    });
    onNext();
  };

  const handleBack = () => {
    const filledKeywords = keywords.filter((k: string) => k.trim());
    const filledReferences = references.filter((r: string) => r.trim());
    setFormData({
      ...formData,
      title,
      content,
      abstractImages,
      imagePosition,
      keywords: filledKeywords,
      references: filledReferences,
    });
    onBack();
  };

  /***************************
   * Effects
   ***************************/
  // Effect to add special symbols popup button to title editor
  useEffect(() => {
    if (titleQuillRef.current && !symbolsAdded.title) {
      const quill = titleQuillRef.current.getEditor();
      addSymbolPopupButton(quill, specialSymbols, "title");

      const editorElement = quill.root;
      if (editorElement) {
        editorElement.style.textTransform = "uppercase";
      }

      setSymbolsAdded((prev) => ({ ...prev, title: true }));
    }
  }, [title, symbolsAdded.title]);

  // Effect to add special symbols popup button to content editor
  useEffect(() => {
    if (contentQuillRef.current && !symbolsAdded.content) {
      const quill = contentQuillRef.current.getEditor();
      addSymbolPopupButton(quill, specialSymbols, "content");
      setSymbolsAdded((prev) => ({ ...prev, content: true }));
    }
  }, [content, symbolsAdded.content]);

  useEffect(() => {
    if (title) {
      updateTitleCharCount(title);
    }
  }, [title]);

  useEffect(() => {
    if (content) {
      updateContentWordCount(content);
    }
  }, [content]);

  useEffect(() => {
    if (references.length > 0) {
      setReferenceWordCounts(
        references.map((ref: string) => {
          const words = ref
            .trim()
            .split(/\s+/)
            .filter((word: string) => word.length > 0);
          return words.length;
        }),
      );
    }
  }, [references]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-4 text-xl font-semibold">Abstract Content</h2>
        <p className="mb-6 text-gray-600">
          Please fill up the abstract content accordingly.
          <br />
          Note: All the abstract content must fit into ONE page (including of
          graphic and references, if any.)
        </p>
      </div>

      {/* Title Field */}
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">
          Title*
        </label>
        <div className="border border-gray-300" data-editor="title">
          <ReactQuill
            ref={titleQuillRef}
            theme="snow"
            value={title}
            onChange={(content: any, delta: any, source: any, editor: any) => {
              const plainText = editor.getText();
              const charCount = plainText.length - 1;

              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = content;
              const textNodes = tempDiv.querySelectorAll("*");
              textNodes.forEach((node) => {
                if (node.childNodes.length > 0) {
                  node.childNodes.forEach((child) => {
                    if (child.nodeType === 3) {
                      child.textContent =
                        child.textContent?.toUpperCase() || "";
                    }
                  });
                }
              });
              const uppercasedContent = tempDiv.innerHTML;

              setTitle(uppercasedContent);
              setTitleCharCount(charCount);
            }}
            modules={quillModules}
            placeholder=""
          />
        </div>
        <div
          className={`text-right text-sm mt-1 ${
            titleCharCount > MAX_TITLE_CHARS
              ? "text-red-500 font-semibold"
              : "text-gray-500"
          }`}
        >
          Character: {titleCharCount}/{MAX_TITLE_CHARS}
        </div>
      </div>

      {/* Content Field */}
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">
          Content*
        </label>
        <div className="border border-gray-300" data-editor="content">
          <ReactQuill
            ref={contentQuillRef}
            theme="snow"
            value={content}
            onChange={(content: any, delta: any, source: any, editor: any) => {
              const plainText = editor.getText();
              const words = plainText
                .trim()
                .split(/\s+/)
                .filter((word: any) => word.length > 0);

              setContent(content);
              setContentWordCount(words.length);
            }}
            modules={quillModules}
            placeholder=""
          />
        </div>

        <div
          className={`text-right text-sm mt-1 ${
            contentWordCount > MAX_CONTENT_WORDS
              ? "text-red-500 font-semibold"
              : "text-gray-500"
          }`}
        >
          Words: {contentWordCount}/{MAX_CONTENT_WORDS}
        </div>
      </div>

      {/* Upload Image */}
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">
          Upload Image
        </label>
        <p className="mb-3 text-sm text-gray-500">
          Authors may include up to two images with their abstract submission.
          <br />
          Landscape orientation is recommended for image submissions.
        </p>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageUpload}
          className="hidden"
          id="image-upload"
          disabled={abstractImages.length >= MAX_IMAGES}
        />
        <label
          htmlFor="image-upload"
          className={`inline-block px-4 py-2 bg-blue-900 text-white rounded cursor-pointer hover:bg-blue-800 ${
            abstractImages.length >= MAX_IMAGES
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          Choose File
        </label>

        {/* Image Previews */}
        {abstractImages.length > 0 && (
          <div className="flex gap-4 mt-4">
            {abstractImages.map((abstractImage: any, index: number) => (
              <div
                key={index}
                className="p-2 border border-gray-300 rounded-md"
              >
                <img
                  src={abstractImage.preview}
                  alt={`Image ${index + 1}`}
                  className="w-[246px] h-[152px] object-cover rounded"
                />
                <div className="mt-2">
                  <label className="block mb-1 text-sm text-gray-500">
                    Image title (Optional)
                  </label>
                  <input
                    type="text"
                    value={abstractImage.title || ""}
                    onChange={(e) => handleTitleChange(index, e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-gray-600 truncate max-w-[200px]">
                    {abstractImage.file?.name || `IMAGE0${index + 1}_Data.PNG`}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Position */}
      {abstractImages.length === 2 && (
        <div>
          <label className="block mb-3 text-sm font-medium text-gray-700">
            Please select your preferred display format:
          </label>
          <div className="space-y-3">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="imagePosition"
                value="side_to_side"
                checked={imagePosition === "side_to_side"}
                onChange={(e) => setImagePosition(e.target.value)}
                className="mr-3"
              />
              <img
                className="w-16"
                src={iconSideToSide}
                alt="Side to Side Icon"
              />
              <span className="ml-3 text-sm text-gray-700">Side to Side</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="imagePosition"
                value="up_and_down"
                checked={imagePosition === "up_and_down"}
                onChange={(e) => setImagePosition(e.target.value)}
                className="mr-3"
              />
              <img
                className="w-16"
                src={iconUpAndDown}
                alt="Up and Down Icon"
              />
              <span className="ml-3 text-sm text-gray-700">Up and Down</span>
            </label>
          </div>
        </div>
      )}

      {/* Keywords */}
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">
          Keywords*
        </label>
        <p className="mb-3 text-sm text-gray-500">Minimum 1, Maximum 5</p>
        <div className="space-y-2">
          {keywords.map((keyword: string, index: number) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={keyword}
                onChange={(e) => handleKeywordChange(index, e.target.value)}
                placeholder={`Keyword ${index + 1}`}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {keywords.length > MIN_KEYWORDS && (
                <button
                  type="button"
                  onClick={() => handleRemoveKeyword(index)}
                  className="px-3 py-2 text-red-500 hover:text-red-700"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
        {keywords.length < MAX_KEYWORDS && (
          <button
            type="button"
            onClick={handleAddKeyword}
            className="px-4 py-2 mt-2 text-blue-900 bg-blue-100 rounded hover:bg-blue-200"
          >
            + Add Keyword
          </button>
        )}
      </div>

      {/* References */}
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">
          References
        </label>
        <p className="mb-3 text-sm text-gray-500">Optional, Maximum 5</p>
        <div className="space-y-2">
          {references.map((reference: string, index: number) => (
            <div key={index} className="flex items-start gap-2">
              <div className="flex-1">
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => handleReferenceChange(index, e.target.value)}
                  placeholder={`Reference ${index + 1}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div
                  className={`text-right text-xs mt-1 ${
                    referenceWordCounts[index] > MAX_REFERENCES_WORDS
                      ? "text-red-500 font-semibold"
                      : "text-gray-500"
                  }`}
                >
                  Words: {referenceWordCounts[index] || 0}/
                  {MAX_REFERENCES_WORDS}
                </div>
              </div>
              {references.length > MIN_REFERENCES && (
                <button
                  type="button"
                  onClick={() => handleRemoveReference(index)}
                  className="px-3 py-2 mt-1 text-red-500 hover:text-red-700"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
        {references.length < MAX_REFERENCES && (
          <button
            type="button"
            onClick={handleAddReference}
            className="px-4 py-2 mt-2 text-blue-900 bg-blue-100 rounded hover:bg-blue-200"
          >
            + Add Reference
          </button>
        )}
      </div>

      {/* Special Symbol Popup */}
      {showAllSymbolPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
        >
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Select Special Symbol
                {lastInsertedSymbol && (
                  <span className="ml-3 text-sm text-green-600 animate-pulse">
                    ✓ Inserted: {lastInsertedSymbol}
                  </span>
                )}
              </h3>
              <button
                onClick={() => setShowAllSymbolPopup(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-8 gap-2 mb-4 sm:grid-cols-10">
              {specialSymbols.map((symbol, index) => (
                <button
                  key={`${symbol.char}-${index}`}
                  onClick={() => insertSymbol(symbol.char)}
                  className="p-2 text-lg border rounded hover:bg-blue-100 hover:border-blue-500 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  title={`${symbol.name} (${symbol.char})`}
                  style={{ fontFamily: "Times New Roman, serif" }}
                >
                  {symbol.char}
                </button>
              ))}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowAllSymbolPopup(false)}
                className="px-4 py-2 text-white transition-colors bg-gray-500 rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={handleBack}
          className="px-6 py-2 text-blue-900 bg-blue-100 rounded hover:bg-blue-200"
        >
          ← Author / Co-authors
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="px-6 py-2 text-white bg-blue-900 rounded hover:bg-blue-800"
        >
          Preview & Confirm Submission →
        </button>
      </div>
    </div>
  );
};

export default Step3;
